/**
 * The calls a dataset import makes.
 *
 * Per repo convention there is no shared axios instance; this file owns every
 * request the import performs and the user is identified by an explicit
 * `owner_id`.
 *
 * **Every path here is slash-free**, as is every other API path in the frontend
 * since 2026-09-13 (six older helpers — `get_projects/`, `get_models/`,
 * `save_classification_data/` and the three `get_one_*_by_version_id/` reads —
 * carried one until then). FastAPI answers a slash mismatch with a 307 whose
 * `Location` is built from the proxied `Host`, which behind HTTPS is
 * cross-origin AND mixed content, so the browser refuses to follow it and the
 * call silently returns nothing. There is an nginx `proxy_redirect` covering it,
 * but the cheapest way not to depend on that is not to generate the redirect —
 * and on a multipart POST the redirect also re-sends the whole body.
 *
 * Nothing in here is imported by the executor — see `datasetPlanRunner.js`.
 */
import axios from 'axios';
import { api_labelflow, api_labelflow_training } from '../../../config.jsx';
import { unwrapEnvelope } from '../utils/apiEnvelope';

/** Long, because these are uploads and a CPU ResNet18 forward pass. */
const UPLOAD_TIMEOUT_MS = 180000;
const CLUSTER_TIMEOUT_MS = 180000;
const READ_TIMEOUT_MS = 30000;

const form = (entries) => {
  const data = new FormData();
  Object.entries(entries).forEach(([key, value]) => {
    if (value !== undefined && value !== null) data.append(key, value);
  });
  return data;
};

/** The user's projects, flattened to what a name lookup needs. */
export const listProjects = async (ownerId, signal) => {
  const response = await axios.get(`${api_labelflow}/model_management/get_models`, {
    params: { owner_id: ownerId },
    timeout: READ_TIMEOUT_MS,
    signal,
  });
  const projects = Array.isArray(response?.data) ? response.data : [];
  return projects.map((project) => ({
    id: project.project_id,
    name: project.project_name,
    models: (project.models || []).map((model) => ({ id: model.model_id, name: model.model_name })),
  }));
};

/**
 * Create a project.
 *
 * Returns the raw `{message, data}` — the caller MUST branch on `message`.
 * A duplicate name is HTTP 200 with `data: null`, not an error status.
 */
export const createProject = async ({ name, detail, ownerId }, signal) => {
  const response = await axios.post(
    `${api_labelflow}/project_management/save_project`,
    form({ name, detail: detail || '', owner_id: ownerId }),
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: READ_TIMEOUT_MS, signal },
  );
  return response?.data || {};
};

/** Create a model. Same 200-on-duplicate contract; `data` is the whole doc. */
export const createModel = async ({ name, detail, ownerId, projectId, modelType }, signal) => {
  const response = await axios.post(
    `${api_labelflow}/model_management/save_model`,
    form({
      name,
      detail: detail || '',
      owner_id: ownerId,
      model_type: modelType,
      project_id: projectId,
      visibility: 'private',
    }),
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: READ_TIMEOUT_MS, signal },
  );
  return response?.data || {};
};

/** Upload the images and create the version. `formData` comes from toFormData. */
export const saveClassificationData = async (formData, { signal, onProgress } = {}) => {
  const response = await axios.post(
    `${api_labelflow}/classification_management/save_classification_data`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT_MS,
      signal,
      onUploadProgress: onProgress
        ? (event) => {
            if (!event.total) return;
            onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
          }
        : undefined,
    },
  );
  return response?.data || {};
};

/**
 * Upload a DETECTION dataset and create the version.
 *
 * A separate call from the classification one because the endpoint is
 * different in field names, filename convention and failure mode — see the
 * note above `buildDetectionPayload`.
 */
export const saveDetectionData = async (formData, { signal, onProgress } = {}) => {
  const response = await axios.post(
    `${api_labelflow}/detection_management/save_detection_data`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT_MS,
      signal,
      onUploadProgress: onProgress
        ? (event) => {
            if (!event.total) return;
            onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
          }
        : undefined,
    },
  );
  return response?.data || {};
};

/** Read a detection version back, to confirm the background upload landed. */
export const getDetectionVersion = async (modelInfoId, signal) => {
  const response = await axios.get(
    `${api_labelflow}/detection_management/get_one_detection_model_info_by_version_id`,
    { params: { model_info_id: modelInfoId }, timeout: READ_TIMEOUT_MS, signal },
  );
  return response?.data || {};
};

/** Read a version back — used to confirm the background upload actually landed. */
export const getClassificationVersion = async (modelInfoId, signal) => {
  const response = await axios.get(
    `${api_labelflow}/classification_management/get_one_classification_model_info_by_version_id`,
    { params: { model_info_id: modelInfoId }, timeout: READ_TIMEOUT_MS, signal },
  );
  return response?.data || {};
};

/**
 * Group images by visual similarity.
 *
 * Note the response shape: this route lives in the TRAINING api and wraps its
 * payload in `success_response`, i.e. `{success, message, data}` — so the
 * assignments are at `response.data.data.assignments`, one level deeper than
 * every management-api call above. Reading `response.data.assignments` yields
 * `undefined`, which lands every image in one bucket with nothing to show for
 * it, so the envelope is unwrapped and checked here rather than at the call site.
 */
export const clusterImages = async ({ files, numClusters, maxClusters }, { signal, onProgress } = {}) => {
  const data = new FormData();
  files.forEach((file) => data.append('images', file.blob, file.name));
  if (numClusters) data.append('num_clusters', String(numClusters));
  if (maxClusters) data.append('max_clusters', String(maxClusters));

  const response = await axios.post(`${api_labelflow_training}/cluster/auto`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: CLUSTER_TIMEOUT_MS,
    signal,
    onUploadProgress: onProgress
      ? (event) => {
          if (!event.total) return;
          onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        }
      : undefined,
  });

  // One level deeper than every management-API call above — see apiEnvelope.js.
  return unwrapEnvelope(response?.data, ['assignments']);
};

/**
 * Ask the server to improve the names on a plan the browser already built.
 *
 * Best-effort by contract: this returns `null` for ANY failure — an older
 * deployment with no such route, no API key, a timeout, a malformed reply — and
 * the caller keeps the plan it had. That is what makes the whole feature work
 * with `GEMINI_API_KEY` unset.
 */
export const refinePlan = async ({ instruction, manifest, plan, lang, ownerId }, signal) => {
  try {
    const response = await axios.post(
      `${api_labelflow}/ai_assistant/dataset_plan`,
      { instruction: instruction || '', manifest, plan, lang: lang || null, owner_id: ownerId || null },
      { headers: { 'Content-Type': 'application/json' }, timeout: 45000, signal },
    );
    const body = response?.data || {};
    if (!body.plan) return null;
    return { plan: body.plan, engine: body.engine || 'rulebased', refined: !!body.refined };
  } catch (error) {
    return null;
  }
};

/**
 * Open-vocabulary detection on ONE image.
 *
 * The vocabulary is the user's own words ("helmet", "safety vest"), not class
 * ids — YOLO-World matches text. One image per request is the endpoint's
 * contract, which is why the caller paces and caches rather than fanning out.
 *
 * The reply is a training-API envelope, so the payload is one level deeper.
 */
export const detectCustom = async ({ blob, name, classes, confidence }, { signal } = {}) => {
  const data = new FormData();
  data.append('image', blob, name || 'image.jpg');
  data.append('custom_classes', JSON.stringify(classes));
  if (confidence != null) data.append('confidence', String(confidence));

  const response = await axios.post(`${api_labelflow_training}/auto-detect/detect/custom`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: CLUSTER_TIMEOUT_MS,
    signal,
  });
  return unwrapEnvelope(response?.data, ['detections']);
};

/**
 * Queue training for a version.
 *
 * Named `queue`, not `start`, because that is what it does: the task waits on a
 * Redis GPU semaphore and is drained by Celery Beat. Promising "start" sets an
 * expectation the infrastructure does not keep.
 *
 * `stratify` is passed explicitly. It defaults to FALSE server-side, and a
 * sequential 70/20/10 split can then put none of a small class in validation —
 * which is exactly the shape of a dataset this flow produces.
 */
export const queueTraining = async ({ versionId, modelType, ownerId, epochs }, { signal } = {}) => {
  const path = modelType === 'bounding_box' ? 'detection' : 'cls';
  const body = new URLSearchParams();
  body.append('model_data_info_id', versionId);
  body.append('epochs', String(epochs || (modelType === 'bounding_box' ? 50 : 20)));
  body.append('stratify', '1');
  if (ownerId) body.append('requested_by', ownerId);

  const response = await axios.post(
    `${api_labelflow_training}/training_management/${path}/start`,
    body,
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, timeout: READ_TIMEOUT_MS, signal },
  );
  return response?.data || {};
};

/** Everything the executor needs, as one injectable object. */
const datasetApi = {
  listProjects,
  createProject,
  createModel,
  saveClassificationData,
  getClassificationVersion,
  saveDetectionData,
  getDetectionVersion,
  clusterImages,
  detectCustom,
  queueTraining,
};

export default datasetApi;
